import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileDown, Mail, Loader2 } from "lucide-react";
import { destinataires } from "@/lib/parties";

export default function PdfActions({ etat, onSent, compact }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState({});
  const [copie, setCopie] = useState(true);

  const visible = destinataires(etat);

  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/etats/${etat.id}/pdf`, { responseType: "blob" });
      const name = /filename="([^"]+)"/.exec(r.headers["content-disposition"] || "")?.[1] || "etat-des-lieux.pdf";
      const url = URL.createObjectURL(r.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: name });
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error("Impossible de générer le PDF"); }
    finally { setBusy(false); }
  };

  const send = async () => {
    const destinataires = Object.keys(sel).filter((k) => sel[k]);
    if (!destinataires.length && !copie) return toast.error("Choisissez au moins un destinataire");
    setBusy(true);
    try {
      const { data } = await api.post(`/etats/${etat.id}/send-pdf`, { destinataires, copie_gerant: copie });
      const ok = data.envois.filter((e) => e.ok).length;
      const ko = data.envois.length - ok;
      if (ok) toast.success(`PDF envoyé à ${ok} destinataire${ok > 1 ? "s" : ""}`);
      if (ko) toast.error(`${ko} envoi${ko > 1 ? "s" : ""} en échec`);
      setOpen(false);
      onSent?.(data.envois);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={download} disabled={busy} data-testid="pdf-download-btn"
          className={`${compact ? "h-9" : "h-11"} border-dd-blue text-dd-blue`}>
          {busy ? <Loader2 size={16} className="mr-1 animate-spin" /> : <FileDown size={16} className="mr-1" />} PDF
        </Button>
        <Button onClick={() => setOpen(true)} disabled={busy} data-testid="pdf-email-btn"
          className={`${compact ? "h-9" : "h-11"} bg-dd-blue hover:bg-dd-night text-white`}>
          <Mail size={16} className="mr-1" /> Envoyer par e-mail
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-dd-blue">Envoyer le procès-verbal PDF</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {visible.map(({ key, label, email, nom }) => (
                <label key={key} className={`flex items-start gap-3 text-sm ${email ? "cursor-pointer" : "opacity-60"}`}>
                  <Checkbox checked={!!sel[key]} disabled={!email} data-testid={`send-dest-${key}`}
                    onCheckedChange={(v) => setSel((s) => ({ ...s, [key]: !!v }))} />
                  <span>
                    <span className="font-medium text-dd-night">{label}</span>{nom ? ` — ${nom}` : ""}
                    <span className="block text-dd-warm">{email || "Aucune adresse e-mail saisie (onglet Informations)"}</span>
                  </span>
                </label>
            ))}
            <label className="flex items-center gap-3 text-sm cursor-pointer pt-2 border-t border-dd-light">
              <Checkbox checked={copie} onCheckedChange={(v) => setCopie(!!v)} data-testid="send-copie-gerant" />
              <span className="text-dd-night">M'envoyer une copie</span>
            </label>
          </div>
          <DialogFooter>
            <Button onClick={send} disabled={busy} data-testid="send-pdf-confirm" className="bg-dd-blue text-white hover:bg-dd-night h-11">
              {busy ? <Loader2 size={16} className="mr-1 animate-spin" /> : <Mail size={16} className="mr-1" />} Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
